import { Point } from '../types';
import { getStroke as perfectFreehandGetStroke } from 'perfect-freehand';

// Performance optimization interfaces
export interface DrawingOptimizations {
  // Performance settings
  enablePrediction: boolean;
  predictionSteps: number;
  enableSmoothing: boolean;
  smoothingLevel: number;
  enablePressureSmoothing: boolean;
  pressureSmoothingLevel: number;
  
  // Quality settings
  minDistance: number;
  maxDistance: number;
  interpolationQuality: number;
  
  // Adaptive settings
  adaptiveQuality: boolean;
  targetFPS: number;
}

export const defaultOptimizations: DrawingOptimizations = {
  enablePrediction: true,
  predictionSteps: 2,
  enableSmoothing: true,
  smoothingLevel: 0.5,
  enablePressureSmoothing: true,
  pressureSmoothingLevel: 0.3,
  minDistance: 1, // Increased from 0.5 to avoid too many points
  maxDistance: 5,
  interpolationQuality: 3,
  adaptiveQuality: true,
  targetFPS: 60,
};

export const getStroke = (points: Point[], options: {
  size: number;
  thinning: number;
  smoothing: number;
  streamline: number;
  easing: (t: number) => number;
  simulatePressure: boolean;
  last: boolean;
}): number[][] => {
  if (points.length < 2) return [];
  
  // Convert Point[] to number[][] format for perfect-freehand
  const perfectFreehandPoints = points.map(point => [
    point.x,
    point.y,
    point.pressure || 0.5
  ]);
  
  // Use perfect-freehand with our options
  const perfectFreehandOptions = {
    size: options.size,
    thinning: options.thinning,
    smoothing: options.smoothing,
    streamline: options.streamline,
    easing: options.easing,
    simulatePressure: options.simulatePressure,
    last: options.last
  };
  
  // Get the stroke using perfect-freehand
  return perfectFreehandGetStroke(perfectFreehandPoints, perfectFreehandOptions);
};

export const createSmoothPath = (points: Point[]): string => {
  if (points.length < 2) {
    return '';
  }
  
  if (points.length === 2) {
    return `M ${points[0].x} ${points[0].y} L ${points[1].x} ${points[1].y}`;
  }
  
  let path = `M ${points[0].x} ${points[0].y}`;
  
  for (let i = 1; i < points.length - 1; i++) {
    const xc = (points[i].x + points[i + 1].x) / 2;
    const yc = (points[i].y + points[i + 1].y) / 2;
    path += ` Q ${points[i].x} ${points[i].y}, ${xc} ${yc}`;
  }
  
  // Add the last point
  const lastPoint = points[points.length - 1];
  path += ` L ${lastPoint.x} ${lastPoint.y}`;
  
  return path;
};

export const smoothPoints = (points: Point[], factor: number): Point[] => {
  if (points.length < 3) return points;
  
  const smoothed: Point[] = [points[0]];
  
  for (let i = 1; i < points.length - 1; i++) {
    const prev = points[i - 1];
    const curr = points[i];
    const next = points[i + 1];
    
    const smoothedPoint: Point = {
      x: curr.x + (prev.x + next.x - 2 * curr.x) * factor,
      y: curr.y + (prev.y + next.y - 2 * curr.y) * factor,
      pressure: curr.pressure,
      tiltX: curr.tiltX,
      tiltY: curr.tiltY,
      timestamp: curr.timestamp,
    };
    
    smoothed.push(smoothedPoint);
  }
  
  smoothed.push(points[points.length - 1]);
  return smoothed;
};

export const getDistance = (a: Point, b: Point): number => {
  return Math.sqrt(Math.pow(b.x - a.x, 2) + Math.pow(b.y - a.y, 2));
};

export const getAngle = (a: Point, b: Point): number => {
  return Math.atan2(b.y - a.y, b.x - a.x);
};

export const interpolatePoints = (from: Point, to: Point, t: number): Point => {
  return {
    x: from.x + (to.x - from.x) * t,
    y: from.y + (to.y - from.y) * t,
    pressure: from.pressure && to.pressure ? 
      from.pressure + (to.pressure - from.pressure) * t : 
      from.pressure || to.pressure || 0.5,
    tiltX: from.tiltX && to.tiltX ? 
      from.tiltX + (to.tiltX - from.tiltX) * t : 
      from.tiltX || to.tiltX,
    tiltY: from.tiltY && to.tiltY ? 
      from.tiltY + (to.tiltY - from.tiltY) * t : 
      from.tiltY || to.tiltY,
    timestamp: to.timestamp || Date.now(),
  };
};

export const detectPenInput = (event: PointerEvent): boolean => {
  // Check if we're in iOS WebView
  const isIOSWebView = /iPad|iPhone|iPod/.test(navigator.userAgent) && 
    (window.parent !== window || (window as any).webkit?.messageHandlers);
  
  // iOS WebView debug logging
  const DEBUG_IOS_PEN = isIOSWebView && localStorage.getItem('debug_ios_pen') === 'true';
  
  if (DEBUG_IOS_PEN) {
    console.log('[iOS Pen Debug]', {
      pointerType: event.pointerType,
      pressure: event.pressure,
      tiltX: event.tiltX,
      tiltY: event.tiltY,
      width: (event as any).width,
      height: (event as any).height,
      radiusX: (event as any).radiusX,
      radiusY: (event as any).radiusY,
      clientX: event.clientX,
      clientY: event.clientY,
      isPrecise: (event.clientX % 1 !== 0) || (event.clientY % 1 !== 0)
    });
  }
  
  // Primary pen detection - most reliable
  if (event.pointerType === 'pen') return true;
  
  // Check for pen indicators (tilt, twist, precise pressure)
  if (event.tiltX !== 0 || event.tiltY !== 0) return true;
  if ('twist' in event && (event as any).twist !== 0) return true;
  
  // Advanced detection for devices that misreport pen as mouse/touch
  // Pen characteristics: variable pressure (not binary), high precision
  const hasPenPressure = event.pressure > 0.01 && event.pressure < 0.99;
  const hasHighPrecision = (event.clientX % 1 !== 0) || (event.clientY % 1 !== 0);
  
  // iOS WebView specific detection
  if (isIOSWebView) {
    // iOS often reports Apple Pencil as touch with specific characteristics
    if (event.pointerType === 'touch') {
      // Check for iOS pen patterns:
      // 1. Precise coordinates (sub-pixel precision)
      // 2. Consistent small touch area
      // 3. Pressure between 0 and 1 (not exactly 0 or 1)
      const isPreciseTouch = hasHighPrecision;
      const hasValidPressure = event.pressure > 0 && event.pressure < 1;
      
      // Check touch radius if available
      if ('radiusX' in event && 'radiusY' in event) {
        const radiusX = (event as any).radiusX || 0;
        const radiusY = (event as any).radiusY || 0;
        // Apple Pencil typically has very small radius
        const hasSmallRadius = radiusX < 5 && radiusY < 5 && radiusX > 0;
        
        if (hasSmallRadius && (isPreciseTouch || hasValidPressure)) {
          return true;
        }
      }
      
      // Fallback: if touch has pen-like characteristics
      if (isPreciseTouch && hasValidPressure) {
        return true;
      }
    }
    
    // iOS sometimes reports pen as mouse in WebView
    if (event.pointerType === 'mouse' && hasPenPressure) {
      return true;
    }
  }
  
  if (event.pointerType === 'mouse' && hasPenPressure) {
    // Likely a pen misreported as mouse (common on some devices)
    return true;
  }
  
  if (event.pointerType === 'touch') {
    // Some devices report pen as touch with specific characteristics
    if (hasPenPressure && hasHighPrecision) return true;
    
    // Check touch dimensions to exclude finger/palm
    if ('width' in event && 'height' in event) {
      const width = (event as any).width || 0;
      const height = (event as any).height || 0;
      // Small, precise touch with variable pressure = likely pen
      if (width < 10 && height < 10 && hasPenPressure) return true;
    }
  }
  
  return false;
};

// Detect stylus (Wacom, Surface Pen etc) vs regular pen (Apple Pencil)
export const detectStylus = (event: PointerEvent): boolean => {
  // Stylus has pressure between 0 and 1 (not exactly 0 or 1)
  const pressure = event.pressure;
  
  // Check for stylus-specific pressure patterns (inspired by tldraw)
  const isStylusPressure = (pressure > 0 && pressure < 0.5) || (pressure > 0.5 && pressure < 1);
  
  // If it's detected as pen OR has stylus pressure characteristics
  return detectPenInput(event) || (event.pointerType === 'mouse' && isStylusPressure);
};

// Check if device supports hovering (mouse or pen, not touch)
export const canHover = (event: PointerEvent): boolean => {
  return event.pointerType === 'mouse' || event.pointerType === 'pen';
};

// Check if input is coarse (touch) vs fine (mouse/pen)
export const isCoarsePointer = (event: PointerEvent): boolean => {
  return event.pointerType === 'touch';
};

// Convert number[][] from perfect-freehand to SVG path string
export const getSmoothPath = (strokePoints: number[][]): string => {
  if (strokePoints.length < 2) return '';
  
  // Perfect-freehand returns outline points that form a closed path
  // We need to connect them properly to create a filled shape
  
  if (strokePoints.length === 1) {
    const [x, y] = strokePoints[0];
    return `M ${x.toFixed(2)} ${y.toFixed(2)} Z`;
  }
  
  let pathData = '';
  const firstPoint = strokePoints[0];
  pathData = `M ${firstPoint[0].toFixed(2)} ${firstPoint[1].toFixed(2)}`;
  
  // Connect all outline points
  for (let i = 1; i < strokePoints.length; i++) {
    const [x, y] = strokePoints[i];
    pathData += ` L ${x.toFixed(2)} ${y.toFixed(2)}`;
  }
  
  // Close the path to create a filled shape
  pathData += ' Z';
  
  return pathData;
};

// Convert Point[] to smooth SVG path string 
export const getPointsToSmoothPath = (points: Point[]): string => {
  if (points.length < 2) return createSimplePath(points);
  
  if (points.length === 2) {
    // Simple line for 2 points
    const [start, end] = points;
    return `M ${start.x.toFixed(2)} ${start.y.toFixed(2)} L ${end.x.toFixed(2)} ${end.y.toFixed(2)}`;
  }
  
  // Create smooth curves using quadratic Bézier curves
  let pathData = `M ${points[0].x.toFixed(2)} ${points[0].y.toFixed(2)}`;
  
  for (let i = 1; i < points.length - 1; i++) {
    const current = points[i];
    const next = points[i + 1];
    
    // Control point for smooth curve
    const cpx = (current.x + next.x) / 2;
    const cpy = (current.y + next.y) / 2;
    
    pathData += ` Q ${current.x.toFixed(2)} ${current.y.toFixed(2)} ${cpx.toFixed(2)} ${cpy.toFixed(2)}`;
  }
  
  // End with line to last point
  if (points.length > 1) {
    const lastPoint = points[points.length - 1];
    pathData += ` L ${lastPoint.x.toFixed(2)} ${lastPoint.y.toFixed(2)}`;
  }
  
  return pathData;
};

// Create simple path from Point array (no smoothing)
export const createSimplePath = (points: Point[]): string => {
  if (points.length < 2) return '';
  
  let pathData = `M ${points[0].x.toFixed(2)} ${points[0].y.toFixed(2)}`;
  
  for (let i = 1; i < points.length; i++) {
    pathData += ` L ${points[i].x.toFixed(2)} ${points[i].y.toFixed(2)}`;
  }
  
  return pathData;
};

// Check if pen is hovering (not touching surface)
export const isPenHovering = (event: PointerEvent): boolean => {
  // Pen hovering has pressure of 0 but is still detected as pen type
  return detectPenInput(event) && event.pressure === 0;
};

export const getPressureFromEvent = (event: PointerEvent): number => {
  // For pen devices, use actual pressure with smoothing
  if (event.pointerType === 'pen' || detectPenInput(event)) {
    // Normalize pressure to a more usable range
    // Most pens report 0-1, but some might have different ranges
    const rawPressure = event.pressure;
    
    // Apply a curve to make pressure feel more natural
    // This makes light touches lighter and heavy touches heavier
    const curvedPressure = Math.pow(rawPressure, 1.5);
    
    // Ensure minimum pressure for visibility
    const minPressure = 0.1;
    const maxPressure = 1.0;
    
    return Math.max(minPressure, Math.min(maxPressure, curvedPressure));
  }
  
  // For mouse, use a consistent pressure
  if (event.pointerType === 'mouse') {
    // Check if this might be a pen misreported as mouse
    if (event.pressure > 0 && event.pressure < 1) {
      return event.pressure;
    }
    return 0.8;
  }
  
  // Touch pressure with palm rejection
  if (event.pointerType === 'touch') {
    // Advanced palm detection based on touch characteristics
    if ('width' in event && 'height' in event) {
      const touchWidth = (event as any).width || 0;
      const touchHeight = (event as any).height || 0;
      const touchArea = touchWidth * touchHeight;
      
      // Palm rejection thresholds
      if (touchArea > 200) return 0; // Large area = palm, don't draw
      if (touchWidth > 25 || touchHeight > 25) return 0; // Wide touch = palm
      
      // Normal finger touch
      const normalizedArea = Math.min(1, touchArea / 100); // Normalize to 0-1
      return 0.5 + normalizedArea * 0.3; // Range from 0.5 to 0.8
    }
    return 0.6;
  }
  
  // Default pressure
  return 0.5;
};

export const getTiltFromEvent = (event: PointerEvent): { tiltX: number; tiltY: number } => {
  return {
    tiltX: event.tiltX || 0,
    tiltY: event.tiltY || 0
  };
};

// Helper function to detect palm touches based on multiple factors
export const isPalmTouch = (event: PointerEvent): boolean => {
  if (event.pointerType !== 'touch') return false;
  
  // Check touch area size
  if ('width' in event && 'height' in event) {
    const width = (event as any).width || 0;
    const height = (event as any).height || 0;
    const area = width * height;
    
    // Thresholds for palm detection
    if (area > 150) return true; // Large contact area
    if (width > 20 || height > 20) return true; // Wide or tall touch
    
    // Check aspect ratio - palms tend to be more circular
    if (width > 0 && height > 0) {
      const aspectRatio = Math.max(width, height) / Math.min(width, height);
      if (aspectRatio < 1.5 && area > 100) return true; // Round and large = palm
    }
  }
  
  // Check pressure - palms often have different pressure characteristics
  if (event.pressure === 1 || event.pressure === 0) {
    // Binary pressure often indicates palm or accidental touch
    return true;
  }
  
  return false;
};


// Draw all strokes on canvas
export const drawStrokes = (
  ctx: CanvasRenderingContext2D,
  strokes: any[],
  selectedStrokeId?: string | null,
  hoveredStrokeId?: string | null
) => {
  strokes.forEach(stroke => {
    ctx.save();
    
    // Apply selection/hover styles
    if (stroke.id === selectedStrokeId) {
      ctx.shadowColor = 'rgba(59, 130, 246, 0.5)';
      ctx.shadowBlur = 10;
    } else if (stroke.id === hoveredStrokeId) {
      ctx.shadowColor = 'rgba(156, 163, 175, 0.5)';
      ctx.shadowBlur = 5;
    }
    
    // Set stroke style with better smoothing
    ctx.strokeStyle = stroke.color;
    ctx.lineWidth = stroke.width;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.miterLimit = 10;
    
    // Handle eraser
    if (stroke.tool === 'eraser') {
      ctx.globalCompositeOperation = 'destination-out';
    }
    
    // Draw the path with smooth curves
    if (stroke.points.length > 0) {
      // Enable smoothing
      if ('imageSmoothingEnabled' in ctx) {
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
      }
      
      if (stroke.points.length === 1) {
        // Single point - draw a dot
        ctx.beginPath();
        ctx.arc(stroke.points[0].x, stroke.points[0].y, stroke.width / 2, 0, Math.PI * 2);
        ctx.fill();
      } else {
        // For pencil tool, use smooth path. For shapes, use straight lines
        // Note: circle, cylinder, and ellipse are handled specially in Whiteboard.tsx
        const shapeTools = ['rectangle', 'triangle', 'arrow', 'line', 'star', 'hexagon', 'pentagon', 'cube', 'pyramid', 'coordinate', 'grid', 'parallelogram', 'trapezoid', 'diamond'];
        const smoothShapeTools = ['circle', 'cylinder', 'ellipse'];
        
        if (!shapeTools.includes(stroke.tool) && !smoothShapeTools.includes(stroke.tool)) {
          const smoothPath = getPointsToSmoothPath(stroke.points);
          const path2D = new Path2D(smoothPath);
          ctx.stroke(path2D);
        } else {
          // For shapes, draw straight lines between points
          ctx.beginPath();
          ctx.moveTo(stroke.points[0].x, stroke.points[0].y);
          for (let i = 1; i < stroke.points.length; i++) {
            ctx.lineTo(stroke.points[i].x, stroke.points[i].y);
          }
          ctx.stroke();
        }
      }
    }
    
    ctx.restore();
  });
};

// Draw current stroke being drawn with smooth curves
export const drawCurrentStroke = (
  ctx: CanvasRenderingContext2D,
  points: Point[],
  tool: string,
  color: string,
  width: number
) => {
  if (points.length === 0) return;
  
  ctx.save();
  
  ctx.strokeStyle = color;
  ctx.lineWidth = width;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.miterLimit = 10;
  ctx.globalAlpha = 1;
  
  if (tool === 'eraser') {
    ctx.globalCompositeOperation = 'destination-out';
  } else {
    ctx.globalCompositeOperation = 'source-over';
  }
  
  // Enable smoothing
  if ('imageSmoothingEnabled' in ctx) {
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
  }
  
  if (points.length === 1) {
    // Single point - draw a dot
    ctx.beginPath();
    ctx.arc(points[0].x, points[0].y, width / 2, 0, Math.PI * 2);
    ctx.fillStyle = color;
    ctx.fill();
  } else {
    // For pencil tool, use smooth path. For shapes, use straight lines
    const shapeTools = ['rectangle', 'triangle', 'arrow', 'line', 'star', 'hexagon', 'pentagon', 'cube', 'pyramid', 'coordinate', 'grid', 'parallelogram', 'trapezoid', 'diamond'];
    const smoothShapeTools = ['circle', 'cylinder', 'ellipse'];
    
    if (!shapeTools.includes(tool) && !smoothShapeTools.includes(tool)) {
      const smoothPath = getPointsToSmoothPath(points);
      const path2D = new Path2D(smoothPath);
      ctx.stroke(path2D);
    } else {
      // For shapes, draw straight lines between points
      ctx.beginPath();
      ctx.moveTo(points[0].x, points[0].y);
      for (let i = 1; i < points.length; i++) {
        ctx.lineTo(points[i].x, points[i].y);
      }
      ctx.stroke();
    }
  }
  ctx.restore();
};

// Draw shape preview
export const drawShapePreview = (
  ctx: CanvasRenderingContext2D,
  tool: string,
  startPoint: Point,
  endPoint: Point,
  color: string,
  width: number
) => {
  ctx.save();
  ctx.strokeStyle = color;
  ctx.lineWidth = width;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.miterLimit = 10;
  ctx.setLineDash([5, 5]);
  
  const dx = endPoint.x - startPoint.x;
  const dy = endPoint.y - startPoint.y;
  
  ctx.beginPath();
  
  switch (tool) {
    case 'rectangle':
      ctx.rect(startPoint.x, startPoint.y, dx, dy);
      break;
      
    case 'circle':
      const radius = Math.sqrt(dx * dx + dy * dy);
      ctx.arc(startPoint.x, startPoint.y, radius, 0, Math.PI * 2);
      break;
      
    case 'line':
      ctx.moveTo(startPoint.x, startPoint.y);
      ctx.lineTo(endPoint.x, endPoint.y);
      break;
      
    case 'arrow':
      // Draw line
      ctx.moveTo(startPoint.x, startPoint.y);
      ctx.lineTo(endPoint.x, endPoint.y);
      
      // Draw arrowhead
      const angle = Math.atan2(dy, dx);
      const headLength = Math.min(20, Math.sqrt(dx * dx + dy * dy) * 0.3);
      
      ctx.moveTo(endPoint.x, endPoint.y);
      ctx.lineTo(
        endPoint.x - headLength * Math.cos(angle - Math.PI / 6),
        endPoint.y - headLength * Math.sin(angle - Math.PI / 6)
      );
      
      ctx.moveTo(endPoint.x, endPoint.y);
      ctx.lineTo(
        endPoint.x - headLength * Math.cos(angle + Math.PI / 6),
        endPoint.y - headLength * Math.sin(angle + Math.PI / 6)
      );
      break;
      
    case 'triangle':
      ctx.moveTo(startPoint.x + dx / 2, startPoint.y);
      ctx.lineTo(startPoint.x, startPoint.y + dy);
      ctx.lineTo(startPoint.x + dx, startPoint.y + dy);
      ctx.closePath();
      break;
  }
  
  ctx.stroke();
  ctx.restore();
};

// ===== PERFORMANCE OPTIMIZATION FUNCTIONS =====
// These are optional functions that can be used for better performance

/**
 * Advanced smoothing using weighted average
 * @param newPoint - The new point to smooth
 * @param lastPoints - Array of recent points for smoothing
 * @param smoothingLevel - Level of smoothing (0-1)
 * @param enablePressureSmoothing - Whether to smooth pressure values
 * @returns Smoothed point
 */
export const applySmoothingFilter = (
  newPoint: Point,
  lastPoints: Point[],
  smoothingLevel: number = 0.5,
  enablePressureSmoothing: boolean = true
): Point => {
  const weight = 1 - smoothingLevel;
  
  // Weighted average with recent points
  let smoothedX = newPoint.x * weight;
  let smoothedY = newPoint.y * weight;
  let remainingWeight = 1 - weight;
  
  for (let i = lastPoints.length - 1; i >= 0 && remainingWeight > 0.01; i--) {
    const pointWeight = remainingWeight * 0.5;
    smoothedX += lastPoints[i].x * pointWeight;
    smoothedY += lastPoints[i].y * pointWeight;
    remainingWeight -= pointWeight;
  }

  // Smooth pressure if enabled
  let smoothedPressure = newPoint.pressure;
  if (enablePressureSmoothing && newPoint.pressure !== undefined && lastPoints.length > 0) {
    const pressureWeights = [0.4, 0.3, 0.2, 0.1, 0.05];
    let totalPressure = newPoint.pressure * pressureWeights[0];
    let totalWeight = pressureWeights[0];
    
    for (let i = 0; i < Math.min(lastPoints.length, pressureWeights.length - 1); i++) {
      const point = lastPoints[lastPoints.length - 1 - i];
      if (point.pressure !== undefined) {
        totalPressure += point.pressure * pressureWeights[i + 1];
        totalWeight += pressureWeights[i + 1];
      }
    }
    
    smoothedPressure = totalPressure / totalWeight;
  }

  return {
    x: smoothedX,
    y: smoothedY,
    pressure: smoothedPressure,
    tiltX: newPoint.tiltX,
    tiltY: newPoint.tiltY,
    timestamp: newPoint.timestamp,
  };
};

/**
 * High-quality interpolation with variable pressure
 * @param start - Start point
 * @param end - End point
 * @param distance - Distance between points
 * @param interpolationQuality - Quality of interpolation
 * @returns Array of interpolated points
 */
export const interpolatePointsHighQuality = (
  start: Point,
  end: Point,
  distance: number,
  interpolationQuality: number = 3
): Point[] => {
  const interpolatedPoints: Point[] = [];
  const steps = Math.ceil(distance / interpolationQuality);
  
  for (let i = 1; i <= steps; i++) {
    const t = i / steps;
    
    // Use cubic interpolation for smoother curves
    const easeT = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
    
    const interpolatedPoint: Point = {
      x: start.x + (end.x - start.x) * easeT,
      y: start.y + (end.y - start.y) * easeT,
      pressure: interpolatePressure(start.pressure, end.pressure, t),
      tiltX: interpolateValue(start.tiltX, end.tiltX, t),
      tiltY: interpolateValue(start.tiltY, end.tiltY, t),
      timestamp: (start.timestamp ?? 0) + ((end.timestamp ?? 0) - (start.timestamp ?? 0)) * t,
    };
    
    interpolatedPoints.push(interpolatedPoint);
  }
  
  return interpolatedPoints;
};

// Helper functions for interpolation
const interpolatePressure = (start: number | undefined, end: number | undefined, t: number): number => {
  const startP = start ?? 0.5;
  const endP = end ?? 0.5;
  return startP + (endP - startP) * t;
};

const interpolateValue = (start: number | undefined, end: number | undefined, t: number): number | undefined => {
  if (start === undefined || end === undefined) return undefined;
  return start + (end - start) * t;
};

/**
 * Optimize canvas context for drawing
 * @param ctx - Canvas rendering context
 */
export const optimizeContext = (ctx: CanvasRenderingContext2D) => {
  // Disable alpha for better performance if not needed
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  
  // Set default line styles
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  
  // Enable hardware acceleration hints
  if ('webkitImageSmoothingEnabled' in ctx) {
    (ctx as any).webkitImageSmoothingEnabled = true;
  }
  
  // Set miter limit for better corner rendering
  ctx.miterLimit = 10;
};

/**
 * Batch draw operations for better performance
 * @param ctx - Canvas rendering context
 * @param operations - Array of drawing operations
 */
export const batchDraw = (
  ctx: CanvasRenderingContext2D,
  operations: (() => void)[]
) => {
  ctx.save();
  
  // Batch all operations
  ctx.beginPath();
  operations.forEach(op => op());
  ctx.stroke();
  
  ctx.restore();
};